import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {LoadingSpinner} from 'components';
import {
  selectProviderSessions,
  selectProviderSessionsPagination,
  selectProviderSessionsStatus,
} from 'features/Provider/providerSelectors';
import {
  getProviderSessions,
  providerActions,
} from 'features/Provider/providerSlice';
import {selectUserProfile} from 'features/User';
import {
  GetSessionsFilters,
  MemberProfile,
  SliceStatus,
  UserAccountType,
} from 'interfaces';
import {useTranslation} from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useDispatch, useSelector} from 'react-redux';
import {formatTimeRangeWithOffset} from 'utils';

import {RevenueItem} from '../components/RevenueItem';

const BILLING_SESSION_SIZE = 10;

export default function DPSessions() {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const sessions = useSelector(selectProviderSessions);
  const billingSessionsStatus = useSelector(selectProviderSessionsStatus);
  const {hasNextPage, nextPage} = useSelector(selectProviderSessionsPagination);

  const user = useSelector(selectUserProfile);
  const [filters, _setFilters] = useState<GetSessionsFilters>({
    memberType: UserAccountType.scale,
    paymentType: '',
    insuranceCompany: [],
    dateRange: {from: null, to: null},
    sortBy: 'latest',
    search: '',
    providerId: user?._id ?? '',
    sessionType: 'individual',
  });

  const fetchBillingSessions = useCallback(() => {
    dispatch(
      getProviderSessions({
        pageNo: nextPage,
        size: BILLING_SESSION_SIZE,
        ...filters,
      }),
    );
  }, [dispatch, filters, nextPage]);

  useEffect(() => {
    fetchBillingSessions();
  }, [fetchBillingSessions]);

  const config = {
    dataLength: sessions.length,
    hasMore: hasNextPage,
    next: fetchBillingSessions,
  };

  useEffect(() => {
    return () => {
      // Reset sessions when component unmounts
      dispatch(providerActions.resetProviderSessions());
    };
  }, [dispatch]);

  const memoizedSessions = useMemo(() => sessions, [sessions]);

  useEffect(() => {
    dispatch(providerActions.setMemoizedSessions(memoizedSessions));
  }, [memoizedSessions]);

  const renderLoaders = () => {
    if (billingSessionsStatus === SliceStatus.pending) {
      return (
        <div className="w-full flex justify-center items-center mx-auto space-x-3 my-8">
          <LoadingSpinner width={20} height={20} type="Oval" />
          <h4 className="font-medium text-gray-600">
            {t(
              'transactionHistory.loadingMoreSessions',
              'Loading more sessions...',
            )}
          </h4>
        </div>
      );
    } else if (billingSessionsStatus === SliceStatus.resolved && !hasNextPage) {
      return (
        <p className="text-center font-light py-3">
          {t('transactionHistory.youreAllCaughtUp', 'You’re all caught up!')}
          <span role="img" aria- aria-label="celebration">
            🎊
          </span>
        </p>
      );
    } else {
      return null;
    }
  };

  return (
    <div id="scrollableDiv" className="overflow-y-auto h-full">
      <InfiniteScroll
        {...config}
        endMessage={renderLoaders()}
        loader={
          <div className="w-full flex items-center justify-center my-4">
            <LoadingSpinner height={50} width={50} type="Oval" />
          </div>
        }
        scrollableTarget="scrollableDiv"
      >
        {memoizedSessions?.map(session => {
          return (
            <RevenueItem
              key={session?._id}
              client={session?.patientDetails?.fullName}
              memberType={session?.patientDetails?.accountType}
              appointmentType={session?.appointmentType}
              insuranceName={
                session?.patientDetails?.insuranceDetails?.companyName ?? 'N/A'
              }
              appointmentLabel={session?.appointmentLabel}
              sessionDate={session?.sessionDate}
              sessionTime={formatTimeRangeWithOffset(
                session?.sessionDate,
                Number(session?.duration),
                false,
              )}
              duration={session?.duration}
              sessionId={session?.appointmentId}
              user={session?.patientDetails as MemberProfile}
              isBillable={session?.isBillable}
              stages={session?.stages?.map(stage => ({
                name: stage?.stage,
                status: stage?.stageStatus,
                shouldBeChecked: stage?.shouldBeChecked,
                tooltip: stage?.tooltip,
              }))}
            />
          );
        })}
      </InfiniteScroll>
    </div>
  );
}
